import type { ViewEndpoint } from "../protocol/events";
import { log } from "../shared/debug";

/**
 * Keep the screen on while a session runs.
 *
 * Scanning is a hands-on-the-cards activity: nothing touches the screen for long
 * stretches, which is exactly what a phone's auto-lock is waiting for. Once it fires the
 * page goes hidden, the session stops, and the user comes back to a stopped scanner —
 * and, on iOS, where the camera permission is per visit by default, to another prompt.
 * A screen wake lock is the browser's word for "this page is being watched". It is held
 * from `session:started` to `session:stopped` and never longer, so an idle page still
 * locks the way the user set it to.
 *
 * Advisory in both directions. The browser lets go on its own when the page is hidden or
 * power saving intervenes, and it may refuse one outright — iOS did, in home-screen web
 * apps, before 18.4. A page without a lock scans exactly as it did before this file
 * existed; it just cannot stop the screen going dark. So every outcome is a log line and
 * never a message: there is nothing the user could do about it, and nothing about the
 * scan is affected.
 */
export function mountWakeLock(endpoint: ViewEndpoint): void {
  const wakeLock = navigator.wakeLock as WakeLock | undefined;
  if (!wakeLock) {
    log("ui", "screen wake lock unsupported — the screen may lock during a session");
    return;
  }
  /** True while a session runs — the only time a lock is wanted. */
  let wanted = false;
  let held: WakeLockSentinel | null = null;
  let requesting = false;
  /** Counts sessions, so a request outlived by its session can tell it is stale. */
  let epoch = 0;

  const acquire = (): void => {
    if (!wanted || held || requesting || document.hidden) return;
    requesting = true;
    const at = epoch;
    void wakeLock
      .request("screen")
      .then((sentinel) => {
        // A stop arrived while the request was in flight: let go at once.
        if (!wanted) return sentinel.release();
        held = sentinel;
        sentinel.addEventListener("release", () => {
          if (held !== sentinel) return;
          held = null;
          // The page hid, or power saving stepped in. Whether to ask again is decided on
          // the next `session:started` or visibility change, not here.
          log("ui", "screen wake lock released by the browser");
        });
        log("ui", "screen wake lock held");
      })
      .catch((error: unknown) => {
        log("ui", `screen wake lock refused (${(error as Error).message}) — the screen may lock`);
      })
      .finally(() => {
        requesting = false;
        // A session that started while an older session's request was still pending
        // was told to wait; ask again for it, once. A refusal alone never retries.
        if (wanted && !held && epoch !== at) acquire();
      });
  };
  const release = (): void => {
    wanted = false;
    const sentinel = held;
    held = null;
    if (sentinel) void sentinel.release().catch(() => {});
  };

  endpoint.on("session:started", () => {
    wanted = true;
    epoch++;
    acquire();
  });
  endpoint.on("session:stopped", release);
  endpoint.on("camera:failed", release);
  // The browser drops the lock when the page hides; a session still running when it
  // comes back — or, more often, one the view resumed — asks for it again.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) acquire();
  });
}
